import { useMemo, useRef } from 'react';
import { useCardDashboardStationeryTemplatesContainerQuery } from './DesignsGallery.graphql.generated';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer';
import type { StationeryTemplate } from './DesignsGallery.types';
import { processInitialQueryData, themeIdWithOrientation, parseDisplayOrderEntry } from './utils';
import { injectProducts } from './injectProducts';
import type { FacetOptionLocal } from './components/Filters/Filter.types';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';
import { FREE_DIGITAL_DUMMY_PRODUCT } from '@apps/card/components/ProductUIConfig/freeDigitalProduct';
import { useProducts } from './useProducts';
import { useWebsiteThemeId } from './WebsiteThemeIdContext';

type UseDesignTemplatesArgs = {
  eventId?: string;
  categories?: StationeryTemplateCategoryEnum[];
  themeIds?: string[];
  skipDigitalDesigns?: boolean;
};

export type StationeryTemplateWithThemeJson = StationeryTemplate & { themeJSON: ThemeJson };

/*
 * Resolves the `printGalleryThemeDisplayOrder` flag into the ordered theme list
 * for the current category. The payload is either a flat array (applies to every
 * category) or a `{ [category]: string[] }` map. Returns undefined when the flag
 * is off or has no list for this category.
 *
 * Expected object format:
 * {
 *   "saveTheDate": [ ... ],
 *   "enclosure": [
 *     "wide modern_monogram+navy",
 *     "tall upload_your_design",
 *     "weekend_events+blush",
 *     "tall modern_monogram+navy"
 *   ],
 *   ...
 * }
 */
export const resolveThemeDisplayOrder = (value: string | undefined, payload: unknown, currentCategory: StationeryTemplateCategoryEnum): string[] | undefined => {
  const hasDisplayOrder = !!value && value !== 'off';
  if (!hasDisplayOrder) return undefined;
  if (Array.isArray(payload)) return payload as string[];
  if (payload && typeof payload === 'object') {
    const themeIdList = (payload as Record<string, string[]>)[currentCategory];
    if (Array.isArray(themeIdList)) return themeIdList;
  }
  return undefined;
};

/*
 * The ordered theme list from the gallery-order flag for the current category.
 */
export const useThemeDisplayOrder = (): string[] | undefined => {
  const { currentCategory } = useCardCategoryContext();
  const { value, payload } = useFeatureValue('printGalleryThemeDisplayOrder');
  return useMemo(() => resolveThemeDisplayOrder(value, payload, currentCategory), [value, payload, currentCategory]);
};

/*
 * The themeId to promote to the front of the gallery: the `theme` URL param (read
 * once on mount) or the website's theme. Shared so the base sort and the
 * search-inactive expansion promote the same theme.
 */
export const usePriorityThemeId = (): string | undefined => {
  const websiteThemeId = useWebsiteThemeId();
  const [themeQueryParam] = useManagedSearchParam('theme');
  const themeQueryParamRef = useRef(themeQueryParam);
  return themeQueryParamRef.current || websiteThemeId;
};

/*
 * This method allows setting order of top N items in the gallery based on array of themeIds coming from Amplitude Feature flag
 */
const setGalleryOrder = (templates: StationeryTemplate[], themeOrder: string[]): StationeryTemplate[] => {
  if (templates?.length < 1 || themeOrder?.length < 1) return templates;
  try {
    // Use first occurrence so duplicates don't push a theme to a later position.
    // Colorway (`+colorway`) is stripped here — it only matters for the
    // search-inactive repeat expansion, not for ordering the base (deduped) list.
    const themeLookup = new Map<string, number>();
    themeOrder.forEach((entry, index) => {
      const { orientationKey, themeId } = parseDisplayOrderEntry(entry);
      if (!themeLookup.has(orientationKey)) themeLookup.set(orientationKey, index);
      // Only bare entries position a template regardless of orientation; an
      // orientation-prefixed entry must not order a differently-oriented variant.
      if (orientationKey === themeId && !themeLookup.has(themeId)) themeLookup.set(themeId, index);
    });
    return [...templates].sort((a, b) => {
      const aIndex = themeLookup.get(themeIdWithOrientation(a)) ?? themeLookup.get(a?.themeId) ?? Number.POSITIVE_INFINITY;
      const bIndex = themeLookup.get(themeIdWithOrientation(b)) ?? themeLookup.get(b?.themeId) ?? Number.POSITIVE_INFINITY;
      return aIndex - bIndex;
    });
  } catch {
    console.error('Error sorting templates by printGalleryThemeDisplayOrder.  Using default sort order');
    return templates;
  }
};

const useDesignTemplatesCore = (currentCategory: StationeryTemplateCategoryEnum, args: UseDesignTemplatesArgs) => {
  const { eventId, categories, themeIds, skipDigitalDesigns } = args;
  const shouldSkipFetchingDigitalDesignData = !(currentCategory === StationeryTemplateCategoryEnum.invitation || currentCategory === StationeryTemplateCategoryEnum.saveTheDate);

  const { data: templateData, error } = useCardDashboardStationeryTemplatesContainerQuery({
    variables: {
      filter: {
        categories: categories ?? [currentCategory],
        side: 'front',
        themeIds,
      },
      // Requesting a number larger than what we have in the DB while we wait on
      // designs for pagination
      first: 20000,
      skipDigitalDesigns: skipDigitalDesigns || shouldSkipFetchingDigitalDesignData,
      includeOptions: {
        includeFavoritesByEventIds: eventId ? [eventId] : undefined,
        includeProducts: true,
      },
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: false,
  });

  if (error) throw error;

  const templatesWithProductIds = useMemo(() => {
    return processInitialQueryData(templateData, currentCategory);
  }, [templateData, currentCategory]);

  return templatesWithProductIds;
};

export const useDesignTemplates = (args: UseDesignTemplatesArgs) => {
  const { currentCategory } = useCardCategoryContext();
  const templatesWithProductIds = useDesignTemplatesCore(currentCategory, args);
  const products = useProducts();

  const themeOrder = useThemeDisplayOrder();
  const priorityThemeId = usePriorityThemeId();

  const templates = useMemo(() => {
    const templatesWithProducts = injectProducts(templatesWithProductIds, products);

    // Digital-only designs have no purchasable product (empty productIds), so give
    // them the synthetic free basic-digital product. This drives the tile's
    // "Free Digital" price and the `format:digital` tag below through the normal
    // product pipeline, mirroring how the PDP appends it for paper+digital themes.
    for (const template of templatesWithProducts) {
      if (template.format === 'digital' && template.products.length === 0) {
        template.products = [FREE_DIGITAL_DUMMY_PRODUCT];
      }
    }

    // Conceptually part of `processedInitialPrintableTemplates`. However this part can only be done after
    // injectProducts. TODO: Consider all `mappedTags`logic to post-injectProducts.
    for (const template of templatesWithProducts) {
      const templateFormats = new Set<FacetOptionLocal>(template.products.map(p => (isDigitalProduct(p) ? 'format:digital' : 'format:paper')));
      template.mappedTags.push(...Array.from(templateFormats));
    }

    const sortedTemplates: StationeryTemplate[] = themeOrder ? setGalleryOrder(templatesWithProducts, themeOrder) : [...templatesWithProducts];

    // Promoting certain templates to first place
    if (priorityThemeId) {
      const priorityIndex = sortedTemplates.findIndex(template => template.themeId === priorityThemeId);
      if (priorityIndex > 0) {
        const [priorityTemplate] = sortedTemplates.splice(priorityIndex, 1);
        sortedTemplates.unshift(priorityTemplate);
      }
    }

    return sortedTemplates;
  }, [templatesWithProductIds, products, themeOrder, priorityThemeId]);

  return templates;
};
